import { Injectable } from '@angular/core';
import WebApp from '@twa-dev/sdk';
import { valueIsNullOrEmpty } from '../../helper/valueIsNullOrEmpty';
import { environment } from '../../../environments/environment';
import { BehaviorSubject } from 'rxjs';

export interface TelegramUser {
  id: number;
  first_name: string;
  last_name?: string;
  username?: string;
  language_code?: string;
  is_premium?: boolean;
  photo_url?: string;
}

export interface TelegramInitData {
  user?: TelegramUser;
  chat_type?: string;
  chat_instance?: string;
  start_param?: string;
  auth_date: number;
  hash: string;
}

@Injectable({
  providedIn: 'root'
})
export class TelegramService {
  private webApp: typeof WebApp | null = null;
  private initDataParsed: TelegramInitData | null = null;
  private initError: string | null = null;
  viewportHeight$ = new BehaviorSubject<number>(window.innerHeight);

  constructor() {
    this.initializeWebApp();
  }

  /**
   * Инициализация Telegram Web App
   */
  private initializeWebApp(): void {
    try {
      console.log('🔄 TelegramService: Initializing WebApp...');
      
      // Проверяем доступность WebApp SDK
      if (typeof WebApp === 'undefined') {
        this.initError = 'WebApp SDK is not available';
        console.warn('⚠️ TelegramService:', this.initError);
        return;
      }
      
      this.webApp = WebApp;
      console.log('📱 TelegramService: WebApp SDK loaded, initData:', this.webApp.initData ? 'present' : 'empty');
      
      if (this.webApp.initData || !valueIsNullOrEmpty(environment.telegramAuthToken)) {
        this.parseInitData();
        this.webApp.ready();
        this.webApp.expand();
        this.setupViewportListener();
        console.log('✅ TelegramService: Initialized successfully');
      } else {
        console.log('ℹ️ TelegramService: No initData available, running outside Telegram');
      }
    } catch (error: any) {
      this.initError = error?.message || String(error);
      console.error('❌ TelegramService init error:', this.initError);
      alert(`TelegramService init error: ${this.initError}`);
    }
  }

  /**
   * Парсинг данных инициализации Telegram
   */
  private parseInitData(): void {
    try {
      var initData = this.webApp?.initData || '';
      if(valueIsNullOrEmpty(initData) && !valueIsNullOrEmpty(environment.telegramAuthToken) ) {
        initData = environment.telegramAuthToken;
      }
      if (!initData) return;

      console.log('🔍 TelegramService: Parsing initData...');
      const params = new URLSearchParams(initData);
      const user = params.get('user');
      const authDate = params.get('auth_date');
      const hash = params.get('hash');

      if (user && authDate && hash) {
        this.initDataParsed = {
          user: JSON.parse(user),
          chat_type: params.get('chat_type') || undefined,
          chat_instance: params.get('chat_instance') || undefined,
          start_param: params.get('start_param') || undefined,
          auth_date: parseInt(authDate),
          hash: hash
        };
        console.log('✅ TelegramService: InitData parsed, user:', this.initDataParsed.user?.id);
      } else {
        console.log('⚠️ TelegramService: Missing required fields in initData');
      }
    } catch (error: any) {
      const errorMsg = `Ошибка при парсинге Telegram init data: ${error?.message || error}`;
      console.error('❌', errorMsg);
      alert(errorMsg);
    }
  }

  /**
   * Получить токен аутентификации для заголовков
   */
  getAuthToken(): string | null {
    return this.webApp?.initData || null;
  }

  /**
   * Получить данные пользователя Telegram
   */
  getUser(): TelegramUser | null {
    return this.initDataParsed?.user || null;
  }

  /**
   * Получить полные данные инициализации
   */
  getInitData(): TelegramInitData | null {
    return this.initDataParsed;
  }

  /**
   * Проверить, запущено ли приложение в Telegram
   */
  isInTelegram(): boolean {
    return !!this.webApp?.initData;
  }
  
  /**
   * Получить ошибку инициализации (если есть)
   */
  getInitError(): string | null {
    return this.initError;
  }

  /**
   * Показать главную кнопку
   */
  showMainButton(text: string, callback: () => void): void {
    if (!this.webApp) return;
    this.webApp.MainButton.text = text;
    this.webApp.MainButton.show();
    this.webApp.MainButton.onClick(callback);
  }

  /**
   * Скрыть главную кнопку
   */
  hideMainButton(): void {
    if (!this.webApp) return;
    this.webApp.MainButton.hide();
  }

  /**
   * Показать кнопку "Назад"
   */
  showBackButton(callback: () => void): void {
    if (!this.webApp) return;
    this.webApp.BackButton.show();
    this.webApp.BackButton.onClick(callback);
  }

  /**
   * Скрыть кнопку "Назад"
   */
  hideBackButton(): void {
    if (!this.webApp) return;
    this.webApp.BackButton.hide();
  }

  /**
   * Закрыть приложение
   */
  close(): void {
    if (!this.webApp) return;
    this.webApp.close();
  }

  /**
   * Отправить данные в Telegram бота
   */
  sendData(data: string): void {
    if (!this.webApp) return;
    this.webApp.sendData(data);
  }

  /**
   * Показать всплывающее окно
   */
  showAlert(message: string): Promise<void> {
    if (!this.webApp) {
      alert(message);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.webApp!.showAlert(message, resolve);
    });
  }

  /**
   * Показать диалог подтверждения
   */
  showConfirm(message: string): Promise<boolean> {
    if (!this.webApp) {
      return Promise.resolve(confirm(message));
    }
    return new Promise((resolve) => {
      this.webApp!.showConfirm(message, resolve);
    });
  }

  /**
   * Показать всплывающее окно с кнопками
   */
  showPopup(params: {
    title?: string;
    message: string;
    buttons?: {
      id?: string;
    } & (
      | {
          type: "default" | "destructive";
          text: string;
        }
      | {
          type: "ok" | "close" | "cancel";
        }
    )[];
  }): Promise<string> {
    if (!this.webApp) {
      alert(params.message);
      return Promise.resolve('');
    }
    return new Promise((resolve) => {
      this.webApp!.showPopup(params, (id) => resolve(id || ''));
    });
  }

  /**
   * Установить цвет заголовка
   */
  setHeaderColor(color: "bg_color" | "secondary_bg_color" | `#${string}`): void {
    if (!this.webApp) return;
    this.webApp.setHeaderColor(color);
  }

  /**
   * Установить цвет фона
   */
  setBackgroundColor(color: "bg_color" | "secondary_bg_color" | `#${string}`): void {
    if (!this.webApp) return;
    this.webApp.setBackgroundColor(color);
  }

  /**
   * Включить/отключить вертикальные свайпы для закрытия
   */
  enableClosingConfirmation(): void {
    if (!this.webApp) return;
    this.webApp.enableClosingConfirmation();
  }

  /**
   * Отключить подтверждение закрытия
   */
  disableClosingConfirmation(): void {
    if (!this.webApp) return;
    this.webApp.disableClosingConfirmation();
  }

  /**
   * Получить версию Telegram Web App
   */
  getVersion(): string {
    return this.webApp?.version || 'unknown';
  }

  /**
   * Получить платформу пользователя
   */
  getPlatform(): string {
    return this.webApp?.platform || 'unknown';
  }

  /**
   * Получить цветовую схему
   */
  getColorScheme(): 'light' | 'dark' {
    return this.webApp?.colorScheme || 'light';
  }

  /**
   * Получить высоту области просмотра
   */
  getViewportHeight(): number {
    return this.webApp?.viewportHeight || window.innerHeight;
  }

  /**
   * Получить стабильную высоту области просмотра
   */
  getViewportStableHeight(): number {
    return this.webApp?.viewportStableHeight || window.innerHeight;
  }

  /**
   * Проверить, развернуто ли приложение
   */
  isExpanded(): boolean {
    return this.webApp?.isExpanded || false;
  }
  private setupViewportListener(): void {
    if (!this.webApp) return;

    // выставляем сразу текущее значение
    this.viewportHeight$.next(this.webApp.viewportStableHeight || window.innerHeight);

    this.webApp.onEvent('viewportChanged', () => {
      const height = this.webApp?.viewportStableHeight || window.innerHeight;
      console.log('📐 TelegramService: viewportChanged, stableHeight =', height);
      this.viewportHeight$.next(height);
    });
  }

}
